import { useState, useEffect } from "react";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import {
  Check,
  ChevronsUpDown,
  MapPin,
  ExternalLink,
  Lock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { COUNTRIES, normalizeCountryCode } from "@/lib/data/countries";
import { HONDURAS_DEPARTMENTS, getDepartmentByCity } from "@/lib/data/honduras";
import { US_STATES } from "@/lib/data/us-states";
import { useCommonI18n } from "@/hooks/useModuleI18n";

interface LocationFieldProps {
  defaultCountry?: string;
  defaultCity?: string;
  defaultAddress?: string;
  defaultState?: string;
  defaultZipCode?: string;
  defaultMapUrl?: string;
  countryName?: string;
  cityName?: string;
  addressName?: string;
  stateName?: string;
  zipCodeName?: string;
  mapUrlName?: string;
  disabled?: boolean;
  required?: boolean;
  requiredForHonduras?: boolean;
  requiredForUSA?: boolean;
  showAddress?: boolean;
  showMapUrl?: boolean;
  hideZipCode?: boolean;
  className?: string;
  // Filtrado de países y estados - se carga automáticamente de la empresa
  // Usa estos parámetros solo para sobrescribir la configuración
  allowedCountries?: string[];
  allowedStatesByCountry?: Record<string, string[]>;
}

export function LocationField({
  defaultCountry,
  defaultCity = "",
  defaultAddress = "",
  defaultState = "",
  defaultZipCode = "",
  defaultMapUrl = "",
  countryName = "country",
  cityName = "city",
  addressName = "address",
  stateName = "state",
  zipCodeName = "zipCode",
  mapUrlName = "mapUrl",
  disabled = false,
  required = false,
  requiredForHonduras = true,
  requiredForUSA = false,
  showAddress = true,
  showMapUrl = false,
  hideZipCode = false,
  className,
  allowedCountries: allowedCountriesOverride,
  allowedStatesByCountry: allowedStatesByCountryOverride,
}: LocationFieldProps) {
  const { t, location } = useCommonI18n();

  // Cargar configuración de ubicación de la empresa automáticamente
  const locationFilterConfig = useQuery(api.companies.queries.getLocationFilterConfig);

  // Usar override si se proporciona, si no usar el del query
  const allowedCountries = allowedCountriesOverride || locationFilterConfig?.allowedCountries;
  const allowedStatesByCountry = allowedStatesByCountryOverride || locationFilterConfig?.allowedStatesByCountry;
  const shouldHideZipCode = hideZipCode || locationFilterConfig?.hideZipCode;

  const [countryOpen, setCountryOpen] = useState(false);
  const [departmentOpen, setDepartmentOpen] = useState(false);
  const [municipalityOpen, setMunicipalityOpen] = useState(false);
  const [usStateOpen, setUsStateOpen] = useState(false);

  // Búsqueda local — input nativo fuera del Command
  const [countrySearch, setCountrySearch] = useState("");
  const [departmentSearch, setDepartmentSearch] = useState("");
  const [municipalitySearch, setMunicipalitySearch] = useState("");
  const [usStateSearch, setUsStateSearch] = useState("");

  const [country, setCountry] = useState(() =>
    normalizeCountryCode(defaultCountry),
  );

  const isHonduras = country === "HN";
  const isUSA = country === "US";

  const isLocationRequired =
    required ||
    (isHonduras && requiredForHonduras) ||
    (isUSA && requiredForUSA);

  const [department, setDepartment] = useState(() => {
    if (isHonduras && defaultCity) {
      return getDepartmentByCity(defaultCity) ?? "";
    }
    return "";
  });

  const [usState, setUsState] = useState(defaultState);
  const [city, setCity] = useState(defaultCity);
  const [zipCode, setZipCode] = useState(defaultZipCode);
  const [address, setAddress] = useState(defaultAddress);
  const [mapUrl, setMapUrl] = useState(defaultMapUrl);

  useEffect(() => {
    const normalizedCountry = normalizeCountryCode(defaultCountry);
    setCountry(normalizedCountry);
    if (normalizedCountry === "HN" && defaultCity) {
      setDepartment(getDepartmentByCity(defaultCity) ?? "");
    } else {
      setDepartment("");
    }
  }, [defaultCountry, defaultCity]);

  useEffect(() => {
    setCity(defaultCity);
  }, [defaultCity]);
  useEffect(() => {
    setAddress(defaultAddress);
  }, [defaultAddress]);
  useEffect(() => {
    setUsState(defaultState);
  }, [defaultState]);
  useEffect(() => {
    setZipCode(defaultZipCode);
  }, [defaultZipCode]);
  useEffect(() => {
    setMapUrl(defaultMapUrl);
  }, [defaultMapUrl]);

  const selectedCountry = COUNTRIES.find((c) => c.value === country);
  const selectedState = US_STATES.find((s) => s.value === usState);
  const currentDept = HONDURAS_DEPARTMENTS.find((d) => d.value === department);
  const selectedDept = HONDURAS_DEPARTMENTS.find((d) => d.value === department);
  const selectedMunicipality = currentDept?.municipios.find(
    (m) => m.value === city,
  );

  const countriesFiltered = allowedCountries
    ? COUNTRIES.filter((c) => allowedCountries.includes(c.value))
    : COUNTRIES;

  const usStatesFiltered = allowedStatesByCountry?.["US"]
    ? US_STATES.filter((s) => allowedStatesByCountry["US"].includes(s.value))
    : US_STATES;

  const hondurasDepartsFiltered = allowedStatesByCountry?.["HN"]
    ? HONDURAS_DEPARTMENTS.filter((d) =>
        allowedStatesByCountry["HN"].includes(d.value)
      )
    : HONDURAS_DEPARTMENTS;

  const handleCountryChange = (value: string) => {
    setCountry(value);
    setDepartment("");
    setUsState("");
    setCity("");
    setCountryOpen(false);
    setCountrySearch("");
  };

  const handleDepartmentChange = (value: string) => {
    setDepartment(value);
    setCity("");
    setDepartmentOpen(false);
    setDepartmentSearch("");
  };

  const triggerClassName = cn(
    "w-full justify-between font-normal h-10 md:h-9 border-ring/30 bg-background dark:bg-input/30",
    "shadow-[0_4px_16px_rgba(0,0,0,0.1)] hover:shadow-[0_3px_12px_rgba(0,0,0,0.13)]",
    "dark:shadow-[0_4px_16px_rgba(0,0,0,0.15)] dark:hover:shadow-[0_6px_24px_rgba(0,0,0,0.2)]",
    "opacity-70 hover:opacity-100 focus-visible:opacity-100 transition-all",
  );

  const popoverContentClassName = "w-[var(--radix-popover-trigger-width)] p-0";

  return (
    <div className={cn("space-y-4", className)}>
      {/* Country */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2 text-sm font-medium">
          <MapPin className="w-4 h-4" />
          {location.country}
          {isLocationRequired && <Lock className="h-3 w-3 text-primary" />}
        </Label>
        <Popover
          open={countryOpen}
          onOpenChange={(open) => {
            setCountryOpen(open);
            if (!open) setCountrySearch("");
          }}
        >
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={countryOpen}
              disabled={disabled}
              className={triggerClassName}
            >
              {selectedCountry ? (
                <span className="flex items-center gap-2">
                  <span>{selectedCountry.flag}</span>
                  <span>{selectedCountry.label}</span>
                </span>
              ) : (
                <span className="text-muted-foreground">
                  {location.selectCountry}
                </span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className={popoverContentClassName}
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {/* Input nativo fuera del Command para evitar problemas de foco en mobile */}
            <div className="p-2 border-b">
              <input
                type="text"
                placeholder={location.searchCountry}
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
            <Command>
              <CommandList className="max-h-[250px] overflow-y-auto pointer-events-auto" onWheel={(e) => e.stopPropagation()}>
                <CommandEmpty>{location.countryNotFound}</CommandEmpty>
                <CommandGroup>
                  {countriesFiltered.filter(
                    (c) =>
                      countrySearch === "" ||
                      c.label
                        .toLowerCase()
                        .includes(countrySearch.toLowerCase()),
                  ).map((c) => (
                    <CommandItem
                      key={c.value}
                      value={c.value}
                      onSelect={() => handleCountryChange(c.value)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          country === c.value ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="mr-2">{c.flag}</span>
                      {c.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <input type="hidden" name={countryName} value={country} />
      </div>

      {/* Honduras: Department + Municipality */}
      {isHonduras && (
        <div className="grid grid-cols-2 gap-3">
          {/* Department */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              {location.department}
              {isLocationRequired && <Lock className="h-3 w-3 text-primary" />}
            </Label>
            <Popover
              open={departmentOpen}
              onOpenChange={(open) => {
                setDepartmentOpen(open);
                if (!open) setDepartmentSearch("");
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  disabled={disabled}
                  className={triggerClassName}
                >
                  {selectedDept ? (
                    <span>{selectedDept.label}</span>
                  ) : (
                    <span className="text-muted-foreground">
                      {location.selectDepartment}
                    </span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className={popoverContentClassName}
                align="start"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <div className="p-2 border-b">
                  <input
                    type="text"
                    placeholder={location.searchDepartment}
                    value={departmentSearch}
                    onChange={(e) => setDepartmentSearch(e.target.value)}
                    className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>
                <Command>
                  <CommandList className="max-h-[250px] overflow-y-auto pointer-events-auto" onWheel={(e) => e.stopPropagation()}>
                    <CommandEmpty>
                      {location.departmentNotFound}
                    </CommandEmpty>
                    <CommandGroup>
                      {hondurasDepartsFiltered.filter(
                        (d) =>
                          departmentSearch === "" ||
                          d.label
                            .toLowerCase()
                            .includes(departmentSearch.toLowerCase()),
                      ).map((dept) => (
                        <CommandItem
                          key={dept.value}
                          value={dept.value}
                          onSelect={() => handleDepartmentChange(dept.value)}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              department === dept.value
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                          {dept.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Municipality */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              {location.municipality}
              {isLocationRequired && <Lock className="h-3 w-3 text-primary" />}
            </Label>
            <Popover
              open={municipalityOpen}
              onOpenChange={(open) => {
                if (!department) return;
                setMunicipalityOpen(open);
                if (!open) setMunicipalitySearch("");
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  disabled={disabled || !department}
                  className={triggerClassName}
                >
                  {selectedMunicipality ? (
                    <span>{selectedMunicipality.label}</span>
                  ) : (
                    <span className="text-muted-foreground">
                      {department
                        ? location.selectMunicipality
                        : location.selectDepartmentFirst}
                    </span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className={popoverContentClassName}
                align="start"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <div className="p-2 border-b">
                  <input
                    type="text"
                    placeholder={location.searchMunicipality}
                    value={municipalitySearch}
                    onChange={(e) => setMunicipalitySearch(e.target.value)}
                    className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>
                <Command>
                  <CommandList className="max-h-[250px] overflow-y-auto pointer-events-auto" onWheel={(e) => e.stopPropagation()}>
                    <CommandEmpty>
                      {location.municipalityNotFound}
                    </CommandEmpty>
                    <CommandGroup>
                      {currentDept?.municipios
                        .filter(
                          (m) =>
                            municipalitySearch === "" ||
                            m.label
                              .toLowerCase()
                              .includes(municipalitySearch.toLowerCase()),
                        )
                        .map((m) => (
                          <CommandItem
                            key={m.value}
                            value={m.value}
                            onSelect={() => {
                              setCity(m.value);
                              setMunicipalityOpen(false);
                              setMunicipalitySearch("");
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                city === m.value ? "opacity-100" : "opacity-0",
                              )}
                            />
                            {m.label}
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Zip Code */}
          {!shouldHideZipCode && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {location.zipCode}
              </Label>
              <Input
                name={zipCodeName}
                placeholder={location.zipCodePlaceholder}
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                disabled={disabled}
                maxLength={10}
              />
            </div>
          )}

          <input type="hidden" name={stateName} value={department} />
        </div>
      )}

      {/* USA: State + City */}
      {isUSA && (
        <div className="grid grid-cols-2 gap-3">
          {/* State */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              {location.state}
              {isLocationRequired && <Lock className="h-3 w-3 text-primary" />}
            </Label>
            <Popover
              open={usStateOpen}
              onOpenChange={(open) => {
                setUsStateOpen(open);
                if (!open) setUsStateSearch("");
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  disabled={disabled}
                  className={triggerClassName}
                >
                  {selectedState ? (
                    <span>{selectedState.label}</span>
                  ) : (
                    <span className="text-muted-foreground">
                      {location.selectState}
                    </span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className={popoverContentClassName}
                align="start"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <div className="p-2 border-b">
                  <input
                    type="text"
                    placeholder={location.searchState}
                    value={usStateSearch}
                    onChange={(e) => setUsStateSearch(e.target.value)}
                    className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>
                <Command>
                  <CommandList className="max-h-[250px] overflow-y-auto pointer-events-auto" onWheel={(e) => e.stopPropagation()}>
                    <CommandEmpty>{location.stateNotFound}</CommandEmpty>
                    <CommandGroup>
                      {usStatesFiltered.filter(
                        (s) =>
                          usStateSearch === "" ||
                          s.label
                            .toLowerCase()
                            .includes(usStateSearch.toLowerCase()),
                      ).map((state) => (
                        <CommandItem
                          key={state.value}
                          value={state.value}
                          onSelect={() => {
                            setUsState(state.value);
                            setUsStateOpen(false);
                            setUsStateSearch("");
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              usState === state.value
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                          {state.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <input type="hidden" name={stateName} value={usState} />
          </div>

          {/* City */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              {location.city}
              {isLocationRequired && <Lock className="h-3 w-3 text-primary" />}
            </Label>
            <Input
              placeholder={location.cityPlaceholder}
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={disabled}
              required={isLocationRequired}
            />
          </div>

          {/* Zip Code */}
          {!shouldHideZipCode && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {location.zipCodeUS}
              </Label>
              <Input
                name={zipCodeName}
                placeholder={location.zipCodeUSPlaceholder}
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                disabled={disabled}
                maxLength={10}
              />
            </div>
          )}
        </div>
      )}

      {/* Other countries */}
      {!isHonduras && !isUSA && (
        <div className="space-y-2">
          <Label className="text-sm font-medium flex items-center gap-2">
            {location.city}
            {isLocationRequired && <Lock className="h-3 w-3 text-primary" />}
          </Label>
          <Input
            placeholder={location.cityPlaceholder}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            disabled={disabled}
            required={isLocationRequired}
          />
        </div>
      )}
      <input type="hidden" name={cityName} value={city} />

      {/* Address */}
      {showAddress && (
        <div className="space-y-2">
          <Label className="text-sm font-medium flex items-center gap-2">
            {location.address}
            {isLocationRequired && <Lock className="h-3 w-3 text-primary" />}
          </Label>
          <Textarea
            name={addressName}
            placeholder={location.addressPlaceholder}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            disabled={disabled}
            required={isLocationRequired}
            rows={2}
          />
        </div>
      )}

      {/* Map URL */}
      {showMapUrl && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">{location.mapUrl}</Label>
          <div className="flex gap-2">
            <Input
              name={mapUrlName}
              type="url"
              placeholder={location.mapUrlPlaceholder}
              value={mapUrl}
              onChange={(e) => setMapUrl(e.target.value)}
              disabled={disabled}
              className="flex-1"
            />
            {mapUrl && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => window.open(mapUrl, "_blank")}
                title={location.openInMaps}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
